package com.routexk.quiz.pages

import com.routexk.quiz.ui.utils.rpx

import androidx.compose.runtime.*
import com.routexk.quiz.analytics.FbPixel
import com.routexk.quiz.analytics.Ga4
import com.routexk.quiz.di.LocalAppContainer
import com.routexk.quiz.funnel.FunnelApi
import com.routexk.quiz.funnel.FunnelCopy
import com.routexk.quiz.funnel.FunnelStore
import com.routexk.quiz.funnel.ui.FunnelCard
import com.routexk.quiz.funnel.ui.FunnelCta
import com.routexk.quiz.funnel.ui.FunnelLayout
import com.routexk.quiz.funnel.ui.FunnelProgressBar
import com.routexk.quiz.ui.components.forms.TextInput
import com.routexk.quiz.ui.theme.AppColors
import com.routexk.quiz.ui.theme.AppTypography
import com.routexk.quiz.utils.Validation
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*

@Page("/email")
@Composable
fun EmailCapturePage() {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val scope = rememberCoroutineScope()

    var email by remember { mutableStateOf(FunnelStore.answers.email ?: "") }
    var error by remember { mutableStateOf<String?>(null) }
    var saving by remember { mutableStateOf(false) }
    var inputStarted by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        Ga4.screenView("email_capture", 5)
        FbPixel.pageView()
        FbPixel.track("ViewContent", params = arrayOf("content_name" to "email_capture"))
    }

    fun submit() {
        // The email address itself never goes to GA4 (PII rule)
        Ga4.event("email_submitted")
        if (!Validation.isValidEmail(email)) {
            val errorType = if (email.isBlank()) "empty" else "invalid_format"
            Ga4.event("email_validation_error", "error_type" to errorType)
            FbPixel.trackCustom("EmailError", "error_type" to errorType)
            error = FunnelCopy.EMAIL_ERROR
            return
        }
        saving = true
        FunnelStore.answers = FunnelStore.answers.copy(email = email.trim())
        Ga4.event(
            "generate_lead",
            "currency" to "USD",
            "value" to Ga4.LEAD_VALUE_USD,
            "method" to "quiz_funnel",
        )
        scope.launch {
            // The same event_id rides along in the PATCH so the backend's CAPI
            // Lead deduplicates against the browser event below.
            val leadEventId = FbPixel.newEventId("lead")
            val saved = FunnelApi.saveEmail(
                container.defaultHttpClient,
                email.trim(),
                fbLeadEventId = leadEventId,
                fbp = FbPixel.fbp(),
                fbc = FbPixel.fbc(),
            )
            if (saved) {
                // FB Lead fires only on the server's success response, never on
                // the click — and deliberately without value/currency (§5.6 of
                // the FB tech task: absent, not zero).
                FbPixel.track("Lead", eventId = leadEventId, "content_name" to "quiz_funnel")
                // Advanced Matching: FB hashes the email browser-side
                FbPixel.setUserEmail(email.trim())
            }
            FunnelApi.trackEvent(container.defaultHttpClient, "email_captured")
            saving = false
            ctx.router.navigateTo("/report")
        }
    }

    val mobile = com.routexk.quiz.ui.utils.isMobile()

    // Mobile design: warm background, no progress bar, CTA inside the card
    FunnelLayout(
        title = com.routexk.quiz.i18n.t("RouteXK — Claim Your Savings", "RouteXK — Reclama tu ahorro"),
        fillViewport = true,
        maxContentWidth = 560.rpx,
        warm = mobile
    ) {
        if (!mobile) {
            FunnelProgressBar(
                progress = 0.9,
                onBack = {
                    Ga4.event("email_back_clicked")
                    ctx.router.navigateTo("/results")
                },
                modifier = Modifier.margin(bottom = 32.rpx)
            )
        }

        FunnelCard(Modifier.fillMaxWidth(), padding = 32.rpx, radius = 28.rpx) {
            Column(Modifier.fillMaxWidth().gap(24.rpx)) {
                SpanText(
                    FunnelCopy.EMAIL_TITLE,
                    Modifier
                        .fontSize(24.rpx)
                        .lineHeight(32.rpx)
                        .fontWeight(FontWeight.Bold)
                        .color(com.routexk.quiz.ui.theme.AppColors.Funnel.TextDark)
                )
                TextInput(
                    value = email,
                    onValueChange = {
                        if (!inputStarted) {
                            inputStarted = true
                            Ga4.event("email_input_started")
                        }
                        email = it
                        error = null
                    },
                    placeholder = FunnelCopy.EMAIL_PLACEHOLDER,
                    error = error,
                    autoFocus = true
                )
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = if (mobile) com.varabyte.kobweb.compose.foundation.layout.Arrangement.Center
                        else com.varabyte.kobweb.compose.foundation.layout.Arrangement.Start,
                    modifier = Modifier.gap(8.rpx).fillMaxWidth()
                ) {
                    SpanText(
                        "★",
                        Modifier.fontSize(16.rpx)
                            .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
                    )
                    SpanText(
                        FunnelCopy.EMAIL_PROOF,
                        Modifier.fontSize(14.rpx).lineHeight(16.rpx)
                            .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
                    )
                }

                if (mobile) {
                    EmailCta(saving = saving, onSubmit = ::submit)
                }
            }
        }

        if (!mobile) {
            // Spacer pins the CTA to the bottom of the viewport
            com.varabyte.kobweb.compose.foundation.layout.Box(Modifier.fillMaxWidth().flexGrow(1).minHeight(32.rpx))
            EmailCta(saving = saving, onSubmit = ::submit)
        }
    }
}

@Composable
private fun EmailCta(saving: Boolean, onSubmit: () -> Unit) {
    FunnelCta(
        text = FunnelCopy.CONTINUE,
        loading = saving,
        onClick = onSubmit
    )
}
